import { useEffect, useState } from 'react';

import { Callout } from '@/components/core/Callout';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/core/Select';
import { AttributeComponent } from '@/components/datamodel/AttributeComponent';
import { ForceAttributeEditableProvider } from '@/components/datamodel/hooks/useIsAttributeReadonly';
import { LayoutProvider } from '@/components/datamodel/hooks/useLayout';
import { useTranslation } from '@/hooks/useTranslation';
import {
    DataModelAttribute,
    DataModelAttributeTypeName,
} from '@/types/DataModel';

import { MassUpdateAction, MassUpdateAttributeValue } from '../mass-update.types';

const UpdateAttributeValues = ({
    selectedAttributes,
    setUpdatedValues,
    updatedValues,
}: {
    selectedAttributes: DataModelAttribute[];
    setUpdatedValues: (values: Record<string, MassUpdateAttributeValue>) => void;
    updatedValues: Record<string, MassUpdateAttributeValue>;
}) => {
    const { t } = useTranslation();
    const [attributeActions, setAttributeActions] = useState<{
        [key: string]: string;
    }>({});

    useEffect(() => {
        const _updatedValuesWithActions: Record<string, MassUpdateAttributeValue> = {};
        selectedAttributes.forEach((attribute) => {
            const attributeName = attribute.name;
            const selectedAction = updatedValues[attributeName] ? updatedValues[attributeName].action : null;
            const action =  attributeActions[attributeName] || selectedAction || MassUpdateAction.REPLACE;
            _updatedValuesWithActions[attributeName] = {
                value: null,
                action: action,
            };
        });
        setUpdatedValues(_updatedValuesWithActions);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAttributes, attributeActions]);

    const handleSelectAction = (attributeName: string, action: string) => {
        setAttributeActions((prevActions) => ({
            ...prevActions,
            [attributeName]: action,
        }));
    };

    const isComplexAttribute = (attribute: DataModelAttribute) => {
        return (
            attribute.typeName &&
            [
                DataModelAttributeTypeName.Collection,
                DataModelAttributeTypeName.Group,
                DataModelAttributeTypeName.MultiReference,
                DataModelAttributeTypeName.MultiDimensional,
                DataModelAttributeTypeName.EnumSet,
            ].includes(attribute.typeName)
        );
    };

    const actions = [
        {
            value: MassUpdateAction.ADD,
            label: t('MASS_UPDATE_EDITOR.ACTION.ADD'),
        },
        {
            value: MassUpdateAction.REPLACE,
            label: t('MASS_UPDATE_EDITOR.ACTION.REPLACE'),
        },
        {
            value: MassUpdateAction.CLEAR,
            label: t('MASS_UPDATE_EDITOR.ACTION.CLEAR'),
        },
        {
            value: MassUpdateAction.FIND_AND_REMOVE,
            label: t('MASS_UPDATE_EDITOR.ACTION.FIND_AND_REMOVE'),
        },
    ];

    const attributeTypesRequiringSpecialActions = new Set([
        DataModelAttributeTypeName.Collection,
        DataModelAttributeTypeName.Group,
        DataModelAttributeTypeName.MultiReference,
        DataModelAttributeTypeName.MultiDimensional,
    ]);

    //TO FIX: Disable the ADD and FIND_AND_REMOVE action from the attributeTypesRequiringSpecialActions for now because they are not working properly
    const getActions = (attribute: DataModelAttribute) => {
        if (attribute.typeName && attributeTypesRequiringSpecialActions.has(attribute.typeName)) {
            return actions.filter(action =>
                action.value === MassUpdateAction.ADD ||
                action.value === MassUpdateAction.REPLACE ||
                action.value === MassUpdateAction.CLEAR
            );
        }
        return actions;
    };

    return (
        <div className="tw-flex-1 tw-flex tw-flex-col tw-h-full tw-overflow-hidden">
            <Callout className="tw-my-6" variant="info">
                {t('MASS_UPDATE_EDITOR.EDIT_ATTRIBUTES.DESCRIPTION')}
            </Callout>
            <span className="tw-flex tw-font-bold tw-my-6">
                {t('MASS_UPDATE_EDITOR.EDIT_ATTRIBUTES.TEXT')}
            </span>
            <div className="tw-flex-1 tw-h-full tw-overflow-y-auto">
                <ForceAttributeEditableProvider value={true}>
                    <LayoutProvider layoutName="mass_update">
                        <div>
                            {selectedAttributes.map(
                                (attribute: DataModelAttribute, index: number) => (
                                    <div
                                        className="tw-flex tw-flex-col tw-w-full tw-mb-10 last:tw-mb-0"
                                        key={index}
                                    >
                                        <AttributeComponent
                                            attribute={attribute}
                                            disableUrlSync={true}
                                            beforeAttributeControl={
                                                isComplexAttribute(attribute) && (
                                                    <div>
                                                        <Select
                                                            onValueChange={(
                                                                selectedAction,
                                                            ) =>
                                                                handleSelectAction(
                                                                    attribute.name,
                                                                    selectedAction,
                                                                )
                                                            }
                                                            defaultValue = { updatedValues[attribute.name] ? updatedValues[attribute.name].action : MassUpdateAction.REPLACE }
                                                        >
                                                            <SelectTrigger className="tw-flex-none tw-w-1/4 tw-mb-2">
                                                                <SelectValue
                                                                    placeholder={t(
                                                                        'MASS_UPDATE_EDITOR.ACTION.ACTION.SELECT.PLACEHOLDER',
                                                                    )}
                                                                />
                                                            </SelectTrigger>
                                                            <SelectContent id='actionSelect'>
                                                                {getActions(attribute).map(
                                                                    (
                                                                        action: {
                                                                        value: string;
                                                                        label: string;
                                                                    },
                                                                        index: number,
                                                                    ) => (
                                                                        <SelectItem
                                                                            value={
                                                                                action.value
                                                                            }
                                                                            key={index}
                                                                        >
                                                                            {
                                                                                action.label
                                                                            }
                                                                        </SelectItem>
                                                                    ),
                                                                )}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                )
                                            }
                                        />
                                    </div>
                                ))}
                        </div>
                    </LayoutProvider>
                </ForceAttributeEditableProvider>
            </div>
        </div>
    );
};

export default UpdateAttributeValues;
